<template>
  <div>
    <div class="py-3"></div>
    <v-card class="mx-auto" max-width="95%">
      <v-overlay :value="loading" absolute opacity="0.7">
        <v-progress-circular indeterminate size="48" />
      </v-overlay>
      <v-toolbar color="light-blue darken-3" dark>
        <v-toolbar-title>하우스 애드 · 광고 관리</v-toolbar-title>
        <v-spacer />
        <v-btn color="white" outlined @click="openRegisterDialog" class="mr-2" :disabled="loading">
          <v-icon left>mdi-plus</v-icon>
          새 광고 등록
        </v-btn>
        <v-btn color="white" outlined @click="saveAll" :disabled="loading">
          <v-icon left>mdi-content-save</v-icon>
          저장
        </v-btn>
      </v-toolbar>
      <v-card-text class="pa-4">
        <p class="text-body-2 grey--text mb-4">
          앱별로 <strong>설치형</strong>/<strong>실행형</strong>과 <strong>보상</strong>을 설정하고, <strong>노출매체 선택</strong> 버튼으로 특정 앱에 노출할지 여부를 설정하세요. 순서는 왼쪽 드래그 아이콘을 잡고 끌어서 변경할 수 있습니다.
        </p>

        <draggable
          v-if="appList.length > 0 && draggableRows.length > 0"
          v-model="draggableRows"
          handle=".drag-handle"
          class="py-0"
          @end="onDragEnd"
        >
          <div
            v-for="(row, index) in draggableRows"
            :key="row.app.id"
          >
            <v-list-item
              class="align-center px-0"
              style="min-height: 72px;"
              :class="{ 'grey lighten-4': row.config.active === 'N' }"
            >
              <div class="d-flex align-center flex-grow-1 flex-wrap">
                <!-- 드래그 핸들 -->
                <v-icon class="drag-handle mr-2 grey--text" style="cursor: move;">mdi-drag</v-icon>
                <!-- 썸네일 -->
                <v-avatar size="48" rounded class="mr-3">
                  <img v-if="row.app.thumbnail" :src="thumbUrl(row.app.thumbnail)" alt="" style="object-fit:cover" />
                  <v-icon v-else size="28" color="grey">mdi-image-off</v-icon>
                </v-avatar>
                <!-- 이름 -->
                <div class="mr-3" style="min-width: 120px;">
                  <span class="font-weight-medium">{{ row.app.appName || '(앱 이름 없음)' }}</span>
                  <div class="text-caption grey--text">ID: {{ row.app.id }}</div>
                </div>
                <!-- 유형 -->
                <v-select
                  :value="row.config.adType"
                  :items="adTypeOptions"
                  item-text="text"
                  item-value="value"
                  label="유형"
                  dense
                  outlined
                  hide-details
                  class="mr-2"
                  style="max-width: 180px;"
                  @input="v => updateConfig(row.app.id, { adType: v })"
                />
                <!-- 보상 -->
                <v-select
                  :value="row.config.rewardTypeId || ''"
                  :items="rewardTypeOptions"
                  item-text="name"
                  item-value="id"
                  label="보상"
                  dense
                  outlined
                  hide-details
                  clearable
                  class="mr-2"
                  style="max-width: 180px;"
                  @input="v => updateConfig(row.app.id, { rewardTypeId: v || null })"
                />
                <!-- 노출매체 선택 버튼 (노출 수/전체 포함) -->
                <v-btn outlined small @click="openExposureDialog(row.app.id)">
                  <v-icon left small>mdi-cellphone-sound</v-icon>
                  노출매체 선택 ( {{ getExposureSummary(row.app.id).exposed }} / {{ getExposureSummary(row.app.id).total }} )
                </v-btn>
                <v-spacer />
                <!-- 활성/비활성 (오른쪽 끝, 여백 확보) -->
                <div class="d-flex align-center shrink mr-3">
                  <v-switch
                    :input-value="(row.config.active !== 'N')"
                    :label="(row.config.active !== 'N') ? '활성' : '비활성'"
                    hide-details
                    class="mt-0"
                    @change="v => updateConfig(row.app.id, { active: v ? 'Y' : 'N' })"
                  />
                </div>
              </div>
            </v-list-item>
            <v-divider v-if="index < draggableRows.length - 1" />
          </div>
        </draggable>

        <!-- 등록된 광고 없음 + 새 광고 등록 유도 -->
        <v-container v-else-if="appList.length > 0 && orderedRows.length === 0" class="text-center py-8">
          <v-icon size="64" color="grey lighten-1">mdi-advertisements</v-icon>
          <p class="text-body-1 grey--text mt-3">등록된 광고가 없습니다.</p>
          <p class="text-caption grey--text">'새 광고 등록'으로 앱을 선택해 광고를 추가하세요.</p>
          <v-btn class="mt-3" color="primary" @click="openRegisterDialog">
            <v-icon left>mdi-plus</v-icon>
            새 광고 등록
          </v-btn>
        </v-container>

        <!-- 새 광고 등록 다이얼로그 -->
        <v-dialog v-model="registerDialog" max-width="500">
          <v-card>
            <v-toolbar color="light-blue darken-3" dark dense>
              <v-toolbar-title class="subtitle-2">새 광고 등록</v-toolbar-title>
              <v-spacer />
              <v-btn icon dark @click="closeRegisterDialog">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-toolbar>
            <v-card-text class="pt-4">
              <p class="text-caption grey--text mb-3">광고로 등록할 앱을 선택하세요. (아직 등록되지 않은 앱만 표시됩니다)</p>
              <v-select
                v-model="registerForm.appId"
                :items="appsNotRegistered"
                :item-text="item => item.appName || '(앱 이름 없음)'"
                item-value="id"
                label="앱 선택"
                dense
                outlined
                hide-details
                clearable
                placeholder="앱을 선택하세요"
              >
                <template v-slot:selection="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar size="32" rounded class="mr-2">
                      <img v-if="item.thumbnail" :src="thumbUrl(item.thumbnail)" alt="" style="object-fit:cover" />
                      <v-icon v-else x-small>mdi-image-off</v-icon>
                    </v-avatar>
                    <span>{{ item.appName || '(앱 이름 없음)' }} (ID: {{ item.id }})</span>
                  </div>
                </template>
                <template v-slot:item="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar size="36" rounded class="mr-2">
                      <img v-if="item.thumbnail" :src="thumbUrl(item.thumbnail)" alt="" style="object-fit:cover" />
                      <v-icon v-else small>mdi-image-off</v-icon>
                    </v-avatar>
                    <span>{{ item.appName || '(앱 이름 없음)' }} (ID: {{ item.id }})</span>
                  </div>
                </template>
              </v-select>
              <v-alert v-if="appsNotRegistered.length === 0" type="info" dense class="mt-2">
                등록 가능한 앱이 없습니다. 모든 앱이 이미 광고로 등록되어 있거나, 앱관리에서 앱을 먼저 등록해 주세요.
              </v-alert>
            </v-card-text>
            <v-divider />
            <v-card-actions>
              <v-spacer />
              <v-btn text @click="closeRegisterDialog">취소</v-btn>
              <v-btn color="primary" :disabled="!registerForm.appId" @click="submitRegister">
                등록
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- 노출매체 선택 팝업 (그리드: 한 줄에 여러 개 표시) -->
        <v-dialog v-model="exposureDialog" max-width="900" persistent scrollable>
          <v-card>
            <v-toolbar color="light-blue darken-3" dark dense>
              <v-toolbar-title class="subtitle-2">노출매체 선택</v-toolbar-title>
              <v-spacer />
              <v-btn icon dark @click="closeExposureDialog">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-toolbar>
            <v-card-text class="pt-3">
              <p class="text-caption grey--text mb-3">
                체크한 앱에서만 이 광고가 노출됩니다.
              </p>
              <v-row>
                <v-col
                  v-for="app in otherAppsForExposure"
                  :key="app.id"
                  cols="12"
                  sm="6"
                  md="4"
                  lg="3"
                  class="py-1"
                >
                  <v-card
                    outlined
                    class="d-flex align-center pa-2 cursor-pointer"
                    :class="{ 'primary lighten-4': isExposedOn(app.id) }"
                    @click="toggleExposure(app.id)"
                  >
                    <v-checkbox
                      :input-value="isExposedOn(app.id)"
                      hide-details
                      dense
                      class="mt-0 mr-2 shrink"
                      @click.stop
                      @change="toggleExposure(app.id)"
                    />
                    <v-avatar size="40" rounded class="shrink">
                      <img v-if="app.thumbnail" :src="thumbUrl(app.thumbnail)" alt="" style="object-fit:cover" />
                      <v-icon v-else small>mdi-image-off</v-icon>
                    </v-avatar>
                    <div class="flex-grow-1 min-width-0">
                      <div class="text-body-2 text-truncate">{{ app.appName || '(앱 이름 없음)' }}</div>
                      <div class="text-caption grey--text">ID: {{ app.id }}</div>
                    </div>
                  </v-card>
                </v-col>
              </v-row>
            </v-card-text>
            <v-divider />
            <v-card-actions>
              <v-spacer />
              <v-btn text @click="closeExposureDialog">취소</v-btn>
              <v-btn color="primary" @click="saveAndCloseExposure">
                <v-icon left small>mdi-content-save</v-icon>
                저장
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-container v-if="appList.length === 0" class="text-center py-8">
          <v-icon size="64" color="grey lighten-1">mdi-cellphone-link</v-icon>
          <p class="text-body-1 grey--text mt-3">등록된 앱이 없습니다.</p>
          <p class="text-caption grey--text">'앱관리'에서 앱을 등록한 뒤 광고 설정을 하세요.</p>
          <v-btn class="mt-3" color="primary" to="/advertisement_house_app_list">앱관리로 이동</v-btn>
        </v-container>

        <v-container v-else-if="rewardTypes.length === 0" class="text-center py-6">
          <p class="text-body-2 grey--text">보상 타입이 없으면 보상을 연결할 수 없습니다. '보상 타입 관리'에서 먼저 등록하세요.</p>
          <v-btn class="mt-2" color="primary" to="/advertisement_house_reward_type">보상 타입 관리로 이동</v-btn>
        </v-container>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import draggable from 'vuedraggable';

const S3_BASE = 'https://content-filecenter.s3.ap-northeast-2.amazonaws.com/';

const AD_TYPE_OPTIONS = [
  { text: '설치형 (설치 시 보상)', value: 'install' },
  { text: '실행형 (실행 시 보상)', value: 'run' },
];

export default {
  name: 'HouseAd_AdManage',
  components: { draggable },
  data: () => ({
    selectedRowAppId: null,
    exposureDialog: false,
    registerDialog: false,
    registerForm: { appId: null },
    draggableRows: [],
    adTypeOptions: AD_TYPE_OPTIONS,
    loading: false,
  }),
  computed: {
    appList() {
      return this.$store.state.housead_app_list || [];
    },
    rewardTypes() {
      return this.$store.state.housead_reward_types || [];
    },
    rewardTypeOptions() {
      return [{ id: '', name: '(미선택)' }, ...this.rewardTypes];
    },
    adConfigs() {
      return this.$store.state.housead_ad_configs || [];
    },
    configOrder() {
      return this.$store.state.housead_ad_config_order || [];
    },
    // 등록된 광고만 표시 (config_order에 있는 앱만)
    orderedRows() {
      const orderedIds = this.configOrder.filter(id => this.appList.some(a => a.id === id));
      return orderedIds
        .map(appId => {
          const app = this.appList.find(a => a.id === appId);
          if (!app) return null;
          const config = this.adConfigs.find(c => c.appId === appId) || {
            appId,
            adType: 'install',
            excludedAppIds: [],
            rewardTypeId: null,
            active: 'Y',
          };
          return { app, config };
        })
        .filter(Boolean);
    },
    // 새 광고 등록 시 선택 가능한 앱 (아직 리스트에 없는 앱)
    appsNotRegistered() {
      return this.appList.filter(a => !this.configOrder.includes(a.id));
    },
    // 노출매체: 동일앱 제외, PID DESC
    otherAppsForExposure() {
      if (!this.selectedRowAppId) return [];
      return this.appList
        .filter(a => a.id !== this.selectedRowAppId)
        .sort((a, b) => Number(b.id) - Number(a.id));
    },
  },
  mounted() {
    this.loadHouseAd();
  },
  watch: {
    appList: {
      immediate: true,
      handler(list) {
        if (!list.length) return;
        const order = this.$store.state.housead_ad_config_order || [];
        const hasOrder = order.length > 0 && order.some(id => list.some(a => a.id === id));
        if (!hasOrder) {
          const defaultOrder = list.map(a => a.id).sort((a, b) => Number(b) - Number(a));
          this.$store.commit('SET_HOUSEAD_AD_CONFIG_ORDER', defaultOrder);
        }
      },
    },
    orderedRows: {
      immediate: true,
      handler(rows) {
        this.draggableRows = (rows || []).slice();
      },
    },
  },
  methods: {
    loadHouseAd() {
      this.loading = true;
      this.$store.dispatch('HOUSEAD_FETCH_CMS_INIT')
        .catch((err) => {
          alert(err && err.message ? err.message : '하우스 애드 데이터를 불러오지 못했습니다.');
        })
        .finally(() => { this.loading = false; });
    },
    thumbUrl(path) {
      if (!path) return '';
      return path.startsWith('http') ? path : S3_BASE + path;
    },
    updateConfig(appId, patch) {
      const current = this.adConfigs.find(x => x.appId === appId) || {
        appId,
        adType: 'install',
        excludedAppIds: [],
        rewardTypeId: null,
      };
      const adType = patch.adType !== undefined ? patch.adType : current.adType;
      const rewardTypeId = patch.rewardTypeId !== undefined ? patch.rewardTypeId : current.rewardTypeId;
      const excludedAppIds = patch.excludedAppIds !== undefined ? patch.excludedAppIds : current.excludedAppIds;
      const active = patch.active !== undefined ? patch.active : (current.active !== undefined ? current.active : 'Y');
      this.$store.commit('SET_HOUSEAD_AD_CONFIG', { appId, adType, rewardTypeId, excludedAppIds, active });
    },
    getExposureSummary(appId) {
      const otherApps = this.appList.filter(a => a.id !== appId);
      const total = otherApps.length;
      const config = this.adConfigs.find(c => c.appId === appId);
      const excluded = (config && config.excludedAppIds) || [];
      const exposed = otherApps.filter(a => !excluded.includes(a.id)).length;
      return { exposed, total };
    },
    openExposureDialog(appId) {
      this.selectedRowAppId = appId;
      this.exposureDialog = true;
    },
    closeExposureDialog() {
      this.exposureDialog = false;
      this.selectedRowAppId = null;
    },
    saveAndCloseExposure() {
      this.saveExposure();
      this.closeExposureDialog();
    },
    isExposedOn(appId) {
      const config = this.adConfigs.find(c => c.appId === this.selectedRowAppId);
      const excluded = (config && config.excludedAppIds) || [];
      return !excluded.includes(appId);
    },
    toggleExposure(appId) {
      const config = this.adConfigs.find(c => c.appId === this.selectedRowAppId) || {
        appId: this.selectedRowAppId,
        adType: 'install',
        excludedAppIds: [],
        rewardTypeId: null,
      };
      const excluded = (config.excludedAppIds || []).slice();
      const idx = excluded.indexOf(appId);
      if (idx >= 0) excluded.splice(idx, 1);
      else excluded.push(appId);
      this.$store.commit('SET_HOUSEAD_AD_CONFIG', {
        appId: this.selectedRowAppId,
        adType: config.adType,
        rewardTypeId: config.rewardTypeId,
        excludedAppIds: excluded,
        active: config.active !== undefined ? config.active : 'Y',
      });
    },
    saveExposure() {
      this.loading = true;
      this.$store.dispatch('HOUSEAD_PUT_AD_CONFIGS')
        .then(() => {
          this.closeExposureDialog();
          alert('노출매체 설정이 저장되었습니다.');
        })
        .catch((err) => { alert(err && err.message ? err.message : '저장에 실패했습니다.'); })
        .finally(() => { this.loading = false; });
    },
    saveAll() {
      this.loading = true;
      this.$store.dispatch('HOUSEAD_PUT_AD_CONFIGS')
        .then(() => { alert('광고 설정이 저장되었습니다.'); })
        .catch((err) => { alert(err && err.message ? err.message : '저장에 실패했습니다.'); })
        .finally(() => { this.loading = false; });
    },
    onDragEnd() {
      const newOrder = this.draggableRows.map(r => r.app.id);
      this.$store.commit('SET_HOUSEAD_AD_CONFIG_ORDER', newOrder);
    },
    openRegisterDialog() {
      this.registerForm.appId = null;
      this.registerDialog = true;
    },
    closeRegisterDialog() {
      this.registerDialog = false;
      this.registerForm.appId = null;
    },
    submitRegister() {
      const appId = this.registerForm.appId;
      if (!appId) return;
      this.$store.commit('SET_HOUSEAD_AD_CONFIG', {
        appId,
        adType: 'install',
        excludedAppIds: [],
        rewardTypeId: null,
        active: 'Y',
      });
      const order = this.configOrder.slice();
      if (!order.includes(appId)) {
        order.unshift(appId);
        this.$store.commit('SET_HOUSEAD_AD_CONFIG_ORDER', order);
      }
      this.closeRegisterDialog();
    },
  },
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
.min-width-0 {
  min-width: 0;
}
</style>
